<template>
    <div class="friend">
        <div class="friendInfos">
            <div class="friendNameStatus">
                <div class="friendName">{{ fullName }}</div>
                <v-chip v-if="showStatus" :color="statusColor" text-color="white" small class="friendStatus">{{ statusLabel }}</v-chip>
                <font-awesome-icon v-if="showApprouved" :icon="icons.check" class="fa-fw fa-lg" style="color: green;"></font-awesome-icon>
            </div>
            <div class="friendEmail">{{ email }}</div>
        </div>
        <div class="friendSettings">
            <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon v-bind="attrs" v-on="on" :icon="icons.portfolios" class="fa-fw fa-lg" :color="portfolioIconColor"></font-awesome-icon>
                </template>
                <span>{{$tc('friendZone.manageSharingDetailsDialog.portfolios.tooltip', portfoliosShared)}}</span>
            </v-tooltip>
            <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon v-bind="attrs" v-on="on" :icon="icons.transactions" class="fa-fw fa-lg" :color="transactionIconColor"></font-awesome-icon>
                </template>
                <span>{{$tc('friendZone.manageSharingDetailsDialog.transactions.tooltip', transactionsShared)}}</span>
            </v-tooltip>
            <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon v-bind="attrs" v-on="on" :icon="icons.amounts" class="fa-fw fa-lg" :color="amountIconColor"></font-awesome-icon>
                </template>
                <span>{{$tc('friendZone.manageSharingDetailsDialog.confidential.amounts.tooltip', amountsShared)}}</span>
            </v-tooltip>
        </div>
        <div v-if="status" class="friendActions">
            <div v-if="incomingRequest" class="actions showAlways">
                <v-btn color="green" icon small dark @click="acceptRequest">
                    <font-awesome-icon :icon="icons.accept" class="fa-fw fa-lg"></font-awesome-icon>
                </v-btn>
                <v-btn color="error" icon small dark @click="rejectRequestAsked">
                    <font-awesome-icon :icon="icons.reject" class="fa-fw fa-lg"></font-awesome-icon>
                </v-btn>
            </div>
            <div v-else class="actions">
                <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                            <FriendSharingDetailsDialog :friend="friend" @updated="sharingDetailsUpdated"></FriendSharingDetailsDialog>
                        </div>
                    </template>
                    <span>{{$t('friendZone.manageSharingDetailsDialog.settingsTooltip')}}</span>
                </v-tooltip>
                <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn color="error" icon small dark @click="removeFriendAsked" v-bind="attrs" v-on="on">
                            <font-awesome-icon :icon="icons.remove" class="fa-fw fa-lg"></font-awesome-icon>
                        </v-btn>
                    </template>
                    <span>{{$t('friendZone.manageSharingDetailsDialog.removeFriendshipTooltip')}}</span>
                </v-tooltip>
            </div>
        </div>
        <ConfirmDialog ref="removeFriendDialog" @answered="removeFriendAnswered"></ConfirmDialog>
    </div>
</template>

<script>
import { faBadgeCheck } from '@fortawesome/pro-duotone-svg-icons';
import { faCheck, faTimes, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { approuveFriendshipRequest, removeFriend } from '../../services/SharingService';
import ConfirmDialog from '../../components/controls/ConfirmDialog.vue';
import FriendSharingDetailsDialog from './FriendSharingDetailsDialog.vue';
import { faSuitcase, faHandshake, faUsdCircle } from '@fortawesome/pro-light-svg-icons';

export default {
	components: { ConfirmDialog, FriendSharingDetailsDialog },
    props: {
        friend: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            icons: {
                remove: faTrashAlt,
                check: faBadgeCheck,
                accept: faCheck,
                reject: faTimes,
                portfolios: faSuitcase,
                transactions: faHandshake,
                amounts: faUsdCircle
            }
        }
    },
    computed: {
        fullName() {
            return this.friend.fullName;
        },
        email() {
            return this.friend.email;
        },
        showStatus() {
            return this.status && this.status !== 'approuved';
        },
        showApprouved() {
            return this.status === 'approuved';
        },
        status() {
            return this.friend.status;
        },
        statusLabel() {
            let status;
            switch (this.status) {
                case 'pending':
                    if (this.friend.invitationDirection === 'incoming') {
                        status = 'incomingRequest';
                    } else {
                        status = 'outgoingRequest';
                    }
                    break;
                case 'approuved':
                    status = 'approuved';
                    break;
                default:
                    status = 'none';
                    break;
            }
            return this.$t(`friendZone.status.${status}`);
        },
        incomingRequest() {
            return this.status === 'pending' && this.friend.invitationDirection === 'incoming';
        },
        statusColor() {
            switch (this.status) {
                case 'approuved':
                    return 'green';
                case 'pending':
                    if (this.friend.invitationDirection === 'incoming') {
                        return 'warning';
                    } else {
                        return 'grey';
                    }
                default:
                    return 'grey';
            }
        },
        portfoliosShared() {
            return this.friend.portfolioSharingDetails && this.friend.portfolioSharingDetails.portfolios && this.friend.portfolioSharingDetails.portfolios.active;
        },
        portfolioIconColor() {
            return this.portfoliosShared ? '#000' : '#ccc';
        },
        transactionsShared() {
            return this.friend.portfolioSharingDetails && this.friend.portfolioSharingDetails.transactions && this.friend.portfolioSharingDetails.transactions.active;
        },
        transactionIconColor() {
            return this.transactionsShared ? '#000' : '#ccc';
        },
        amountsShared() {
            return this.friend.portfolioSharingDetails && this.friend.portfolioSharingDetails.confidential && this.friend.portfolioSharingDetails.confidential.shareAmount;
        },
        amountIconColor() {
            return this.amountsShared ? '#000' : '#ccc';
        }
    },
    methods: {
        configShare() {
            this.$store.dispatch('shareWithFriend', { friendId: this.friend.id });
        },
        removeFriendAsked() {
            this.$refs.removeFriendDialog.open({
                title: this.$t('friendZone.removeFriendDialog.title'),
                text: this.$t('friendZone.removeFriendDialog.text', { friendName: this.fullName })
            });
        },
        removeFriendAnswered({ response }) {
            if (response) {
                this.remove();
            }
        },
        remove() {
            removeFriend({ friendUserId: this.friend.id }).then(() => {
                this.$store.dispatch('removeFriend', { friendId: this.friend.id });
            });
        },
        acceptRequest() {
            approuveFriendshipRequest({ requesterUserId: this.friend.id }).then(() => {
                this.$store.dispatch('updateFriendStatus', { friendId: this.friend.id, status: 'approuved' });
            });
        },
        rejectRequestAsked() {
            this.$refs.removeFriendDialog.open({
                title: this.$t('friendZone.rejectFriendRequestDialog.title'),
                text: this.$t('friendZone.rejectFriendRequestDialog.text', { friendName: this.fullName })
            });
        },
        sharingDetailsUpdated({ portfolioSharingDetails }) {
            this.$store.dispatch('updateFriendPortfolioSharingDetails', { friendId: this.friend.id, portfolioSharingDetails });
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../assets/styles/breakpoints.scss";
.friend {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 20px;

    .friendInfos {
        display: flex;
        flex-direction: column;
        flex: 1;
        .friendNameStatus {
            display: flex;
            align-items: center;
            gap: 10px;
            .friendName {
                font-weight: bold;
                font-size: 16px;
            }
            .friendStatus {
                text-transform: capitalize;
                &:empty {
                    display: none;
                }
            }
        }
        .friendEmail {
            font-size: 14px;
            color: #666;
        }
    }
    .friendSettings {
        display: flex;
        gap: 15px;
    }
    .friendActions{
        width: 100px;
        display: flex;
        justify-content: flex-end;
        .actions {
            display: none;
            gap: 15px;
            &.showAlways {
                display: flex;
            }
        }
    }
    &:hover {
        .actions {
            display: flex;
        }
    }
    @media screen and (hover: none) and (pointer: coarse)  {
        .friendActions {
            .actions {
                display: flex;
            }
        }
    }
}
</style>